import {
  ActiveQuestionnaireSection,
  getEmptyActiveQuestionnaireSection,
  getEmptySectionDataRecord
} from 'reducers/activesection/activesectionSlice'

import { SectionDataRecord } from 'share/domain/case'

function saveSectionDataRecordHelperToSessionStorage(
  key: string,
  rec: SectionDataRecord
): void {
  const sessionStor = window.sessionStorage

  sessionStor.setItem(key + '-id', rec.id.toString())
  sessionStor.setItem(key + '-section-id', rec.sectionId.toString())
  if (rec.questionKey) {
    sessionStor.setItem(key + '-question-key', rec.questionKey)
  } else {
    sessionStor.removeItem(key + '-question-key')
  }
  if (rec.questionDataJsonText) {
    sessionStor.setItem(
      key + '-question-data-json-text',
      rec.questionDataJsonText
    )
  } else {
    sessionStor.removeItem(key + '-question-data-json-text')
  }
  sessionStor.setItem(key + '-question-complete-flag', rec.id.toString())
}

function removeSectionDataRecordHelperFromSessionStorage(): void {
  const sessionStor = window.sessionStorage

  // assume that there will never be more than 20 questions
  for (let index = 0; index < 20; index++) {
    const key = 'asection-data-' + index.toString()
    sessionStor.removeItem(key + '-id')
    sessionStor.removeItem(key + '-section-id')
    sessionStor.removeItem(key + '-question-key')
    sessionStor.removeItem(key + '-question-data-json-text')
    sessionStor.removeItem(key + '-question-complete-flag')
  }
}

export function saveActiveSectionToSessionStorage(
  payload: ActiveQuestionnaireSection
): void {
  const sessionStor = window.sessionStorage
  sessionStor.setItem(
    'asection-isactivesection',
    payload.isActiveSection.toString()
  )

  if (payload.section) {
    sessionStor.setItem('asection-id', payload.section.id.toString())

    if (payload.section.sectionSystemName) {
      sessionStor.setItem(
        'asection-system-name',
        payload.section.sectionSystemName
      )
    } else {
      sessionStor.removeItem('asection-system-name')
    }

    if (payload.section.sectionName) {
      sessionStor.setItem('asection-name', payload.section.sectionName)
    } else {
      sessionStor.removeItem('asection-name')
    }

    sessionStor.setItem(
      'asection-status',
      payload.section.sectionStatus.toString()
    )

    if (payload.section.sectionDescription) {
      sessionStor.setItem('asection-descr', payload.section.sectionDescription)
    } else {
      sessionStor.removeItem('asection-descr')
    }

    sessionStor.setItem(
      'asection-order',
      payload.section.sectionOrder.toString()
    )

    if (payload.section.version) {
      sessionStor.setItem(
        'asection-version',
        payload.section.version.toString()
      )
    } else {
      sessionStor.removeItem('asection-version')
    }

    if (payload.section.comments) {
      sessionStor.setItem('asection-comm', payload.section.comments)
    } else {
      sessionStor.removeItem('asection-comm')
    }

    if (payload.section.currentActiveQuestionId) {
      sessionStor.setItem(
        'asection-current-active-question-id',
        payload.section.currentActiveQuestionId.toString()
      )
    } else {
      sessionStor.removeItem('asection-current-active-question-id')
    }

    if (payload.section.currentActiveGroup) {
      sessionStor.setItem(
        'asection-current-active-group',
        payload.section.currentActiveGroup.toString()
      )
    } else {
      sessionStor.removeItem('asection-current-active-group')
    }

    if (payload.section.sectionUiTransformedModel) {
      sessionStor.setItem(
        'asection-ui-transform-model',
        JSON.stringify(payload.section.sectionUiTransformedModel)
      )
    } else {
      sessionStor.removeItem('asection-ui-transform-model')
    }

    if (payload.section.sectionCardSummary) {
      sessionStor.setItem(
        'asection-card-summary',
        payload.section.sectionCardSummary
      )
    } else {
      sessionStor.removeItem('asection-card-summary')
    }

    sessionStor.setItem(
      'asection-isDisplayEnabled',
      payload.section.isDisplayEnabled.toString()
    )

    payload.section.sectionDataRecords.forEach((sectionDataRecord, index) => {
      const key = 'asection-data-' + index.toString()
      saveSectionDataRecordHelperToSessionStorage(key, sectionDataRecord)
    })
  } else {
    sessionStor.removeItem('asection-id')
    sessionStor.removeItem('asection-system-name')
    sessionStor.removeItem('asection-name')
    sessionStor.removeItem('asection-status')
    sessionStor.removeItem('asection-descr')
    sessionStor.removeItem('asection-order')
    sessionStor.removeItem('asection-version')
    sessionStor.removeItem('asection-comm')
    sessionStor.removeItem('asection-current-active-question-id')
    sessionStor.removeItem('asection-ui-transform-model')
    sessionStor.removeItem('asection-card-summary')
    sessionStor.removeItem('asection-isDisplayEnabled')

    removeSectionDataRecordHelperFromSessionStorage()
  }
}

function loadSectionDataRecordHelperFromSessionStorage(
  key: string
): SectionDataRecord | null {
  const sessionStor = window.sessionStorage
  const sectionDataRecord = getEmptySectionDataRecord()
  let hasValue = false

  const id = sessionStor.getItem(key + '-id')
  if (id) {
    sectionDataRecord.id = parseInt(id)
    hasValue = true
  }

  const sectionId = sessionStor.getItem(key + '-section-id')
  if (sectionId) {
    sectionDataRecord.sectionId = parseInt(sectionId)
    hasValue = true
  }

  const questionKey = sessionStor.getItem(key + '-question-key')
  if (questionKey) {
    sectionDataRecord.questionKey = questionKey
    hasValue = true
  }

  const questionDataJsonText = sessionStor.getItem(
    key + '-question-data-json-text'
  )
  if (questionDataJsonText) {
    sectionDataRecord.questionDataJsonText = questionDataJsonText
    hasValue = true
  }

  const questionCompleteFlag = sessionStor.getItem(
    key + '-question-complete-flag'
  )
  if (questionCompleteFlag) {
    sectionDataRecord.questionCompleteFlag = questionCompleteFlag === 'true'
  }

  if (hasValue) {
    return sectionDataRecord
  } else {
    return null
  }
}

export function loadActiveSectionFromSessionStorage(): ActiveQuestionnaireSection {
  const sessionStor = window.sessionStorage
  const activeSection = getEmptyActiveQuestionnaireSection()
  activeSection.isActiveSection = false
  const isActiveSection = sessionStor.getItem('asection-isactivesection')
  if (isActiveSection) {
    activeSection.isActiveSection = isActiveSection === 'true'
  }

  if (activeSection.section) {
    const id = sessionStor.getItem('asection-id')
    if (id) {
      activeSection.section.id = parseInt(id)
    }

    const systemName = sessionStor.getItem('asection-system-name')
    if (systemName) {
      activeSection.section.sectionSystemName = systemName
    }

    const name = sessionStor.getItem('asection-name')
    if (name) {
      activeSection.section.sectionName = name
    }

    const status = sessionStor.getItem('asection-status')
    if (status) {
      activeSection.section.sectionStatus = parseInt(status)
    }

    const descr = sessionStor.getItem('asection-descr')
    if (descr) {
      activeSection.section.sectionDescription = descr
    }

    const order = sessionStor.getItem('asection-order')
    if (order) {
      activeSection.section.sectionOrder = parseInt(order)
    }

    const version = sessionStor.getItem('asection-version')
    if (version) {
      activeSection.section.version = parseInt(version)
    }

    const comment = sessionStor.getItem('asection-comm')
    if (comment) {
      activeSection.section.comments = comment
    }

    const cardSummary = sessionStor.getItem('asection-card-summary')
    if (comment) {
      activeSection.section.comments = cardSummary
    }

    const isDisplayEnabled = sessionStor.getItem('asection-isDisplayEnabled')
    if (isDisplayEnabled) {
      activeSection.section.isDisplayEnabled = isDisplayEnabled === 'true'
    }

    const currentActiveQuestionId = sessionStor.getItem(
      'asection-current-active-question-id'
    )
    if (currentActiveQuestionId) {
      activeSection.section.currentActiveQuestionId = parseInt(
        currentActiveQuestionId
      )
    } else {
      activeSection.section.currentActiveQuestionId = null
    }

    const currentActiveGroup = sessionStor.getItem(
      'asection-current-active-group'
    )
    if (currentActiveGroup) {
      activeSection.section.currentActiveGroup = parseInt(currentActiveGroup)
    } else {
      activeSection.section.currentActiveGroup = null
    }

    const uiTransformedModel = sessionStor.getItem(
      'asection-ui-transform-model'
    )
    if (uiTransformedModel) {
      activeSection.section.sectionUiTransformedModel =
        JSON.parse(uiTransformedModel)
    }

    for (let index = 0; index < 20; index++) {
      const key = 'asection-data-' + index.toString()
      const sectionDataRecord =
        loadSectionDataRecordHelperFromSessionStorage(key)
      if (sectionDataRecord) {
        activeSection.section.sectionDataRecords.push(sectionDataRecord)
      } else {
        break
      }
    }
  }
  return activeSection
}

export function saveSectionUiTransformedModelToSessionStorage(
  // eslint-disable-next-line
  payload: any | null
): void {
  const sessionStor = window.sessionStorage
  if (payload) {
    sessionStor.setItem('asection-ui-transform-model', JSON.stringify(payload))
  } else {
    sessionStor.removeItem('asection-ui-transform-model')
  }
}

export function saveSectionCurrentActiveQuestionId(
  payload: number | null
): void {
  const sessionStor = window.sessionStorage
  if (payload) {
    sessionStor.setItem(
      'asection-current-active-question-id',
      payload.toString()
    )
  } else {
    sessionStor.removeItem('asection-current-active-question-id')
  }
}

export function saveSectionCurrentActiveGroup(payload: number | null): void {
  const sessionStor = window.sessionStorage
  if (payload) {
    sessionStor.setItem('asection-current-active-group', payload.toString())
  } else {
    sessionStor.removeItem('asection-current-active-group')
  }
}
